import IndexedDBStorage from './IndexedDBStorage';
import LocalStorageAdapter from './LocalStorageAdapter';

/**
 * Storage Factory
 * Creates and returns appropriate storage instance based on configuration
 * Implements Singleton pattern to ensure single storage instance
 */
class StorageFactory {
    constructor() {
        this.instance = null;
    }

    /**
     * Check if IndexedDB is available in the browser
     * @private
     * @returns {boolean}
     */
    isIndexedDBAvailable() {
        try {
            return (
                typeof indexedDB !== 'undefined' &&
                indexedDB !== null &&
                typeof indexedDB.open === 'function'
            );
        } catch (error) {
            console.warn('IndexedDB availability check failed:', error);
            return false;
        }
    }

    /**
     * Check if localStorage is available in the browser
     * @private
     * @returns {boolean}
     */
    isLocalStorageAvailable() {
        try {
            const test = '__storage_test__';
            window.localStorage.setItem(test, test);
            window.localStorage.removeItem(test);
            return true;
        } catch (error) {
            console.warn('localStorage availability check failed:', error);
            return false;
        }
    }

    /**
     * Create storage instance based on configuration
     * @param {Object} config - Storage configuration
     * @param {boolean} config.USE_INDEXDB - Whether to use IndexedDB
     * @param {string} config.DB_NAME - Database name for IndexedDB
     * @param {number} config.DB_VERSION - Database version for IndexedDB
     * @param {string} config.STORE_NAME - Object store name for IndexedDB
     * @returns {IStorage} Storage instance
     */
    async createStorage(config = {}) {
        const {
            USE_INDEXDB = true,
            DB_NAME = 'picourt-app-db',
            DB_VERSION = 1,
            STORE_NAME = 'key-value-store'
        } = config;

        // Return existing instance if already created
        if (this.instance) {
            return this.instance;
        }

        let storage = null;

        console.log('ℹ️ Storage config:', { USE_INDEXDB });

        // Try IndexedDB first if enabled
        if (USE_INDEXDB && this.isIndexedDBAvailable()) {
            try {
                console.log('Attempting to create IndexedDB storage...');
                storage = new IndexedDBStorage(DB_NAME, DB_VERSION, STORE_NAME);
                // Wait for initialization to complete
                await storage.initPromise;
                console.log('✅ Using IndexedDB for storage');
            } catch (error) {
                console.error('❌ Failed to initialize IndexedDB:', error);
                console.warn('⚠️ Falling back to localStorage');
                storage = null;
            }
        } else {
            if (!USE_INDEXDB) {
                console.log('ℹ️ IndexedDB disabled in config');
            } else {
                console.log('⚠️ IndexedDB not available in browser');
            }
        }

        // Fallback to localStorage
        if (!storage && this.isLocalStorageAvailable()) {
            storage = new LocalStorageAdapter();
            console.log('✅ Using localStorage for storage');
        }

        // No storage available
        if (!storage) {
            throw new Error('No storage mechanism available. Both IndexedDB and localStorage are unavailable.');
        }

        this.instance = storage;
        return storage;
    }

    /**
     * Get the current storage instance
     * @returns {IStorage|null}
     */
    getInstance() {
        return this.instance;
    }

    /**
     * Reset the storage instance (useful for testing)
     */
    reset() {
        if (this.instance && typeof this.instance.close === 'function') {
            this.instance.close();
        }
        this.instance = null;
    }
}

// Export singleton instance
const factory = new StorageFactory();

export default factory;
export { StorageFactory };
